import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import type { GQLOrganizationStats } from '../organizations/useOrganization';
import { GET_DASHBOARD_COUNTS } from '../../../lib/graphql/queries/organizations';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface DashboardStatsGQL {
  emails: number;
  webConversions: number;
  callsTotal: number;
  conversationsTotal: number;
  opportunityCallsTotal: number;
  propertyViewsTotal: number;
  leadCapturesTotal: number;
  emailDeliveredTotal: number;
  emailOpensTotal: number;
  emailClicksTotal: number;
  leadCounts: {
    all: number;
    email: number;
    organic: number;
    ppc: number;
  };
  orgStats: GQLOrganizationStats | null;
}

export const useDashboardStatsGQL = ({ dateRange }: { dateRange: DateRange }) => {
  const { organization } = useOrganizationStore();
  const orgId = organization?.id ?? '';

  const dateFrom = dateRange.from?.toISOString() ?? undefined;
  const dateTo = dateRange.to?.toISOString() ?? undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: countsRaw, loading: countsLoading } = useQuery<any>(GET_DASHBOARD_COUNTS, {
    variables: { organizationId: orgId, dateFrom, dateTo },
    skip: !orgId,
    fetchPolicy: 'cache-first',
  });

  const data: DashboardStatsGQL = useMemo(() => {
    const agg = countsRaw?.organization?.dashboardAggregates;
    // LiveTransfers-primary count — matches the modal. Falls back to the
    // dashboardAggregates Calls-join value only if the stats field is absent.
    const statsLiveTransfers = countsRaw?.organization?.stats?.totalLiveTransfers;

    return {
      emails: agg?.emailDeliveredTotal ?? 0,
      webConversions: agg?.webConversionsTotal ?? 0,
      callsTotal: agg?.callsTotal ?? 0,
      conversationsTotal: agg?.conversationsTotal ?? 0,
      opportunityCallsTotal: agg?.opportunityCallsTotal ?? 0,
      propertyViewsTotal: agg?.propertyViewsTotal ?? 0,
      leadCapturesTotal: agg?.leadCapturesTotal ?? 0,
      emailDeliveredTotal: agg?.emailDeliveredTotal ?? 0,
      emailOpensTotal: agg?.emailOpensTotal ?? 0,
      emailClicksTotal: agg?.emailClicksTotal ?? 0,
      leadCounts: {
        all: agg?.allLeads ?? 0,
        email: agg?.emailLeads ?? 0,
        organic: agg?.organicLeads ?? 0,
        ppc: agg?.ppcLeads ?? 0,
      },
      orgStats: {
        totalLiveTransfers: statsLiveTransfers ?? agg?.liveTransfersTotal ?? 0,
        totalEmails: agg?.emailDeliveredTotal ?? 0,
        totalEmailOpens: agg?.emailOpensTotal ?? 0,
        totalEmailClicks: agg?.emailClicksTotal ?? 0,
        totalPageViews: agg?.totalPageViews ?? 0,
        totalPropertyViews: agg?.totalPropertyViews ?? 0,
        totalSearchPageViews: agg?.totalSearchPageViews ?? 0,
        totalHomeValuations: agg?.totalHomeValuations ?? 0,
        totalSavedProperties: agg?.totalSavedProperties ?? 0,
        // COMMENTED OUT — Active Users / Nurture / Search Alerts widgets (re-add later)
        // Stats below are no longer fetched by GET_DASHBOARD_COUNTS; defaulted to 0
        // so the OrgStats type stays satisfied without an extra GQL round-trip.
        uniqueActiveUsers: 0,
        uniqueContactsCallsAnswered: 0,
        uniqueContactsEmailsOpened: 0,
        uniqueContactsEmailsClicked: 0,
        uniqueContactsPropertyClicked: 0,
        uniqueContactsSearching: 0,
        clientsInCallNurture: 0,
        clientsInEmailNurture: 0,
        totalSearchAlertsContacts: 0,
        totalSearchAlertsCreatedByRemi: 0,
        totalSearchesMadeByUsers: 0,
        totalContacts: 0,
        totalCalls: 0,
        totalSms: agg?.smsTotal ?? 0,
        totalNotes: 0,
        totalEmailPropertyLinkViews: agg?.totalEmailPropertyLinkViews ?? 0,
        totalWebsiteActivity: agg?.totalWebsiteActivity ?? 0,
        uniqueContactsWithWebsiteActivity: 0,
        uniqueContactsWithConversions: 0,
        totalClientTouches: 0,
        totalClientTouchesCalls: 0,
        totalClientTouchesEmails: 0,
        totalClientTouchesWebsiteActivity: 0,
        totalClientTouchesConversions: 0,
        contactsUpdatedToday: 0,
        eventsProcessedToday: 0,
        contactsByStatus: [],
        contactsByLifecycleStage: [],
      } as GQLOrganizationStats,
    };
  }, [countsRaw]);

  const loading = countsLoading;

  return { data, loading };
};
